import { useCallback, useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";

import authApi from "@/lib/auth-api.client";

import {
  resolveMetadata,
  sanitizeFullName,
  sanitizePhoneNumber,
} from "./derive-user";
import type { MeFallbackState } from "./types";

const EMPTY_ME_FALLBACK: MeFallbackState = {
  userAccountId: null,
  fullName: null,
  pictureUrl: null,
  phoneNumber: null,
};

export type UseMeFallbackParams = {
  session: Session | null;
  user: User | null;
  impersonatedUserId: string | null;
  impersonatedMetadata: Record<string, unknown> | null;
};

export type UseMeFallbackResult = {
  meFallback: MeFallbackState;
  refreshMeFallback: () => Promise<void>;
};

// Resolves missing user_account_id / full_name / picture_url / phone_number by
// querying /api/me when Supabase user_metadata is incomplete. Owns its own
// state, the /me fetch, a refresh helper and the effects that trigger fetch /
// reset on session changes.
export function useMeFallback(
  params: UseMeFallbackParams,
): UseMeFallbackResult {
  const { session, user, impersonatedUserId, impersonatedMetadata } = params;

  const [meFallback, setMeFallback] =
    useState<MeFallbackState>(EMPTY_ME_FALLBACK);

  const fetchMeFallback = useCallback(async () => {
    try {
      const { data } = await authApi.get<{
        user?: {
          id?: string;
          first_name?: string | null;
          last_names?: string | null;
          picture_url?: string | null;
          phone_number?: string | null;
        };
      }>("/me");
      const meUser = data?.user;
      if (!meUser?.id) return;
      const composedName = [meUser.first_name, meUser.last_names]
        .filter((part): part is string => !!part && part.trim().length > 0)
        .join(" ")
        .trim();
      const meFallbackPhone =
        typeof meUser.phone_number === "string" &&
        meUser.phone_number.trim().length > 0
          ? meUser.phone_number.trim()
          : null;
      setMeFallback({
        userAccountId: meUser.id,
        fullName: composedName || null,
        pictureUrl: meUser.picture_url ?? null,
        phoneNumber: meFallbackPhone,
      });
    } catch (error) {
      // Non-critical: me fallback failed
    }
  }, []);

  // Reset first so consumers reading `phoneNumber` / `fullName` receive the
  // fresh value (used after updating the profile in profile-settings).
  const refreshMeFallback = useCallback(async () => {
    setMeFallback(EMPTY_ME_FALLBACK);
    await fetchMeFallback();
  }, [fetchMeFallback]);

  const metadata = resolveMetadata(user, impersonatedMetadata);
  const metadataAccountId =
    (metadata.user_account_id as string | undefined) ?? null;
  const sanitizedMetadataFullName = sanitizeFullName(metadata);
  const sanitizedMetadataPhoneNumber = sanitizePhoneNumber(metadata);
  const sessionUserId = session?.user?.id;

  useEffect(() => {
    if (impersonatedUserId) return; // impersonation manages its own flow
    if (!sessionUserId) return;
    const needsAccountId = !metadataAccountId;
    const needsFullName = !sanitizedMetadataFullName;
    const needsPhoneNumber = !sanitizedMetadataPhoneNumber;
    if (!needsAccountId && !needsFullName && !needsPhoneNumber) return;
    if (
      meFallback.userAccountId ||
      meFallback.fullName ||
      meFallback.phoneNumber
    )
      return; // already resolved this session

    fetchMeFallback();
  }, [
    sessionUserId,
    metadataAccountId,
    sanitizedMetadataFullName,
    sanitizedMetadataPhoneNumber,
    meFallback.userAccountId,
    meFallback.fullName,
    meFallback.phoneNumber,
    impersonatedUserId,
    fetchMeFallback,
  ]);

  useEffect(() => {
    if (!sessionUserId && meFallback.userAccountId) {
      setMeFallback(EMPTY_ME_FALLBACK);
    }
  }, [sessionUserId, meFallback.userAccountId]);

  return { meFallback, refreshMeFallback };
}
